import { useState, useEffect } from 'react';
import { cn } from '@/utils/cn';
import { CheckCircle2, Loader2, XCircle, Circle, ClipboardList, Palette, Code2, ShieldCheck, Package, Sparkles, Film, RefreshCw } from 'lucide-react';
import type { NodeOutput, PipelinePauseState } from '@/hooks/use-pipeline-poll';
import { retryFailedAssets } from '@/api/request';

/** 格式化耗时：毫秒 → 可读字符串（<1s 不被 falsy 吞掉） */
function formatDuration(ms: number | undefined): string {
  if (ms === undefined || ms === null || ms < 0) return '0s';
  if (ms < 1000) return '<1s';
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
}

/** 计算运行时长（running状态） */
function getRunningDuration(node: NodeOutput | undefined): number {
  if (!node || node.status !== 'running') return 0;
  const startTime = node.startedAt ? new Date(node.startedAt).getTime() : Date.now();
  return Date.now() - startTime;
}

interface StageDef {
  key: string;
  label: string;
  icon: typeof ClipboardList;
  /** 后端节点 key → 该阶段的映射 */
  nodeKeys: string[];
  /** running 态进行中文案（如「正在生成素材」） */
  runningText: string;
}

/** 标准模式（非视频）流水线六阶段 */
export const BASE_STAGES: StageDef[] = [
  { key: 'planning', label: '方案规划', icon: ClipboardList, nodeKeys: ['planning'], runningText: '正在规划方案' },
  { key: 'prototype_build', label: '原型构建', icon: Code2, nodeKeys: ['prototype_build', 'game_develop', 'game_develop_blueprint', 'prototype_qa'], runningText: '正在构建并验证可玩原型' },
  { key: 'asset_prepare', label: '素材生成', icon: Palette, nodeKeys: ['asset_prepare'], runningText: '正在生成精美素材' },
  { key: 'asset_inject', label: '素材装填', icon: Sparkles, nodeKeys: ['asset_inject'], runningText: '正在装填素材出正式版' },
  { key: 'qa_check', label: '质量检测', icon: ShieldCheck, nodeKeys: ['qa_check'], runningText: '正在质量检测' },
  { key: 'publish', label: '打包发布', icon: Package, nodeKeys: ['publish', 'done'], runningText: '正在打包发布' },
];

/** 视频转试玩模式阶段：与确认对话框统一为 6 阶段
 *  BUG-20260908-PW-002：video-ai-config 归入「素材生成」——确认后方案已定稿，
 *  互动方案阶段只反映 video-confirmation；AI 配置生成与 AI 素材生成同属素材准备环节，
 *  使展示与实际段执行一致（此前确认后仍长时间停留在“互动方案”转圈）。 */
export const VIDEO_STAGES: StageDef[] = [
  { key: 'video_start', label: '视频分析', icon: Film, nodeKeys: ['video-start'], runningText: '正在分析视频' },
  { key: 'video_plan', label: '互动方案', icon: ClipboardList, nodeKeys: ['video-confirmation'], runningText: '正在生成互动方案' },
  { key: 'asset_prepare', label: '素材生成', icon: Palette, nodeKeys: ['video-ai-config', 'video-frame-analyze', 'asset_prepare'], runningText: '正在生成互动配置与素材' },
  { key: 'video_transcode', label: '视频转码', icon: Film, nodeKeys: ['video-transcode'], runningText: '正在转码视频' },
  { key: 'video_assemble', label: '试玩装配', icon: Sparkles, nodeKeys: ['video-assemble'], runningText: '正在装配试玩' },
  { key: 'publish', label: '打包发布', icon: Package, nodeKeys: ['publish', 'done'], runningText: '正在打包发布' },
];

/** 阶段状态 → 极简文本（主页只展示文本，详情去工作台看） */
function statusText(stage: StageDef, status: 'done' | 'running' | 'failed' | 'pending', error?: string): string {
  switch (status) {
    case 'running': return stage.runningText;
    case 'done': {
      const doneLabels: Record<string, string> = {
        asset_prepare: '素材已生成',
        video_transcode: '转码完成',
        video_assemble: '装填完成',
      };
      return doneLabels[stage.key] ?? '成功';
    }
    case 'failed': return error ? `失败: ${error.length > 30 ? error.slice(0, 30) + '…' : error}` : '失败';
    default: return '等待中';
  }
}

interface PipelineProps {
  nodes: Record<string, NodeOutput>;
  /** 暂停状态（BUG-20260810-065：仅边框颜色+文案标注，无按钮；继续由自然语言驱动） */
  pipeline?: PipelinePauseState | null;
  sessionId?: string | null;
  onNodeClick?: (key: string, node: NodeOutput) => void;
  /** 保留兼容旧调用方（继续按钮已移除，不再使用） */
  onResumed?: () => void;
}

/** 根据后端节点数据计算阶段状态。
 *  P1：原型构建阶段统一消费 prototype_build，旧模板/蓝图键仅作为兼容镜像。
 *  DO-NOT-REVERT: BUG-20260805-028 多 nodeKeys 阶段（历史 game_develop|game_develop_blueprint）
 *  是「任一命中即完成」而非「全部命中」——模板轨永远没有 game_develop_blueprint 节点，
 *  every() 会永远判 pending（实测：原型已完成却显示“等待中”）。video 模式同理：
 *  阶段只按实际存在的节点（video-*）判定，不存在的节点不参与。 */
function getStageStatus(nodeMap: Record<string, NodeOutput>, stage: StageDef): 'done' | 'running' | 'failed' | 'pending' {
  const present = stage.nodeKeys.map((k) => nodeMap[k]).filter(Boolean) as NodeOutput[];
  if (present.length === 0) return 'pending';
  if (present.some((n) => n.status === 'failed')) return 'failed';
  if (present.some((n) => n.status === 'running')) return 'running';
  if (present.some((n) => n.status === 'done')) return 'done';
  return 'pending';
}

/** 阶段当前活跃节点（展示耗时/日志/错误用）：优先 running，其次 done/failed，最后第一个存在的。
 *  DO-NOT-REVERT: BUG-20260805-028 历史蓝图轨 running 标记在 game_develop_blueprint 键上，
 *  固定取 nodeKeys[0] 会拿不到运行中节点的计时与日志。 */
function activeNode(nodeMap: Record<string, NodeOutput>, stage: StageDef): NodeOutput | undefined {
  for (const k of stage.nodeKeys) {
    if (nodeMap[k]?.status === 'running') return nodeMap[k];
  }
  for (const k of stage.nodeKeys) {
    if (nodeMap[k]?.status === 'done' || nodeMap[k]?.status === 'failed') return nodeMap[k];
  }
  return stage.nodeKeys.map((k) => nodeMap[k]).find(Boolean);
}

export function normalizeTerminalVideoNodes(nodes: Record<string, NodeOutput>): Record<string, NodeOutput> {
  const terminalDone = nodes.done?.status === 'done' || nodes.publish?.status === 'done';
  if (!terminalDone || !nodes['video-start']) return nodes;
  const normalized = { ...nodes };
  for (const stage of VIDEO_STAGES) {
    for (const key of stage.nodeKeys) {
      const current = normalized[key];
      if (current) normalized[key] = { ...current, status: 'done', progress: 100 };
    }
  }
  return normalized;
}

function StatusIcon({ status }: { status: string }) {
  switch (status) {
    case 'done': return <CheckCircle2 size={18} className="text-neon-green shrink-0" />;
    case 'running': return <Loader2 size={18} className="animate-spin text-neon-cyan shrink-0" />;
    case 'failed': return <XCircle size={18} className="text-red-400 shrink-0" />;
    default: return <Circle size={18} className="text-muted-foreground/30 shrink-0" />;
  }
}

export function Pipeline({ nodes, pipeline, sessionId, onNodeClick, onResumed }: PipelineProps) {
  const [currentTime, setCurrentTime] = useState(Date.now());
  // BUG-20260826-PW-002: 素材段失败单独补图重试状态
  const [retryingAssets, setRetryingAssets] = useState(false);
  const [retryAssetsError, setRetryAssetsError] = useState('');

  /** 素材生成段失败：仅补生成缺失槽位（后端补齐后自动从下一段续跑），不重走全流程 */
  const handleRetryAssets = async () => {
    if (!sessionId || retryingAssets) return;
    setRetryingAssets(true);
    setRetryAssetsError('');
    try {
      const res = await retryFailedAssets(sessionId);
      if (!res.success) throw new Error(res.error ?? '重试请求失败');
      onResumed?.();
    } catch (err) {
      setRetryAssetsError((err as Error).message);
    } finally {
      setRetryingAssets(false);
    }
  };

  // 每秒更新一次时间，用于动态显示运行时长
  useEffect(() => {
    const hasRunning = Object.values(nodes).some(n => n.status === 'running');
    if (!hasRunning) return;

    const timer = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => clearInterval(timer);
  }, [nodes]);

  // 动态阶段选择：视频模式用 VIDEO_STAGES，否则用 BASE_STAGES
  const isVideoMode = !!nodes['video-start'];
  const stages = isVideoMode ? VIDEO_STAGES : BASE_STAGES;

  // 兼容修复前的旧会话：发布终态是权威信号，不能让遗留 video-ai-config=running 永久压住 6/6。
  const effectiveNodes = isVideoMode ? normalizeTerminalVideoNodes(nodes) : nodes;

  const hasRealNodes = Object.values(nodes).some(
    (n) => n.status === 'running' || n.status === 'done' || n.status === 'failed',
  );

  const doneCount = stages.filter((s) => getStageStatus(effectiveNodes, s) === 'done').length;
  const firstFailedStage = stages.findIndex((candidate) => getStageStatus(effectiveNodes, candidate) === 'failed');
  // BUG-20260810-065: 暂停/用户终止统一为 paused 语义（terminatedByUser 仅区分来源）；
  // 只展示边框颜色+文案，是否继续完全由用户自然语言驱动（发送「继续生成」）
  const paused = pipeline?.status === 'paused';
  const terminatedByUser = pipeline?.terminatedByUser === true;

  return (
    <div className="flex flex-col items-center gap-0">
      {/* 进度 */}
      <p className="mb-1.5 text-[11px] text-muted-foreground/60">{doneCount}/{stages.length} 阶段完成</p>

      {stages.map((stage, i) => {
        // A failed stage is a hard barrier. Ignore late/out-of-order events
        // from downstream work so the UI cannot show “prototype failed” while
        // “asset generation” is still spinning.
        const status = firstFailedStage >= 0 && i > firstFailedStage
          ? 'pending'
          : getStageStatus(effectiveNodes, stage);
        const active = activeNode(effectiveNodes, stage);
        const isLast = i === stages.length - 1;
        const Icon = stage.icon;
        // 当前暂停在「该阶段」，边框+文案标注（继续由自然语言驱动）
        const pausedHere = paused && (pipeline?.stage === stage.key || stage.nodeKeys.includes(pipeline?.stage ?? ''));

        return (
          <div key={stage.key} className="flex flex-col items-center">
            <button
              onClick={() => onNodeClick?.(stage.key, { status, ...active } as NodeOutput)}
              disabled={!hasRealNodes}
              className={cn(
                'glass-card flex w-48 items-center gap-2.5 p-2.5 transition-all duration-500',
                !hasRealNodes && 'opacity-40 cursor-default',
                status === 'running' && 'border-neon-cyan/40 bg-neon-cyan/5 shadow-sm shadow-neon-cyan/10',
                status === 'done' && 'border-neon-green/20',
                status === 'failed' && 'border-red-500/30 bg-red-500/5',
                pausedHere && 'border-amber-400/50 bg-amber-400/5',
              )}
            >
              <StatusIcon status={status} />
              <div className="text-left min-w-0 flex-1">
                <p className={cn('text-xs font-medium truncate transition-colors duration-500', status === 'pending' ? 'text-muted-foreground' : 'text-foreground')}>
                  {stage.label}
                </p>
                {/* 极简状态文本：等待中 / 正在生成素材 / 素材已生成 / 成功 / 失败 */}
                <p className={cn(
                  'mt-0.5 text-[10px] transition-colors duration-500',
                  status === 'failed' ? 'text-red-400/90' : status === 'running' ? 'text-neon-cyan/90' : 'text-muted-foreground/60',
                )}>
                  {statusText(stage, status, active?.error)}
                  {pausedHere
                    ? terminatedByUser
                      ? ' · 已暂停，发送「继续生成」恢复'
                      : ' · 已暂停，发送「继续生成」继续'
                    : ''}
                </p>
                {/* 动态显示时长 */}
                {status === 'running' && (
                  <p className="mt-0.5 text-[10px] text-neon-cyan/70 font-mono">
                    ⏱ {formatDuration(getRunningDuration(active))}
                  </p>
                )}
                {status === 'done' && active?.duration !== undefined && (
                  <p className="mt-0.5 text-[10px] text-neon-green/60 font-mono">
                    ✓ {formatDuration(active.duration)}
                  </p>
                )}
                {/* 显示最新日志 */}
                {status === 'running' && active?.logs && active.logs.length > 0 && (
                  <p className="mt-1 text-[9px] text-muted-foreground/70 truncate">
                    {active.logs[active.logs.length - 1].text}
                  </p>
                )}
              </div>
              <Icon size={15} className="text-muted-foreground/25 shrink-0" />
            </button>

            {!isLast && (
              <div className={cn('h-4 w-0.5 transition-colors duration-700', status === 'done' ? 'bg-neon-green/60' : 'bg-border/40')} />
            )}
          </div>
        );
      })}

      {/* 素材生成段失败：单独补图重试入口（不重走全流程） */}
      {effectiveNodes.asset_prepare?.status === 'failed' && sessionId && (
        <div className="mt-3 flex w-48 flex-col items-center gap-1">
          <button
            onClick={handleRetryAssets}
            disabled={retryingAssets}
            className={cn(
              'flex items-center gap-1.5 rounded-md border border-red-500/40 bg-red-500/10 px-2.5 py-1.5 text-[11px] font-medium text-red-300 transition-colors hover:bg-red-500/20',
              retryingAssets && 'cursor-not-allowed opacity-60',
            )}
          >
            {retryingAssets ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            重新生成失败素材
          </button>
          <p className="text-[9px] text-muted-foreground/60">仅补齐缺失槽位，不重跑全流程</p>
          {retryAssetsError && (
            <p className="text-center text-[10px] text-red-400/90">{retryAssetsError}</p>
          )}
        </div>
      )}

      {/* 确认生成前：全阶段置灰占位，提示用户待开始 */}
      {!hasRealNodes && (
        <p className="mt-3 text-[10px] text-muted-foreground/50">确认生成后开始执行</p>
      )}
    </div>
  );
}
